import type { PharosRealtimeNotification, RealtimeNotificationHandler } from '@klareospace/pharos-lib-types';

interface Subscription {
  /** Event(s) to filter on for `type: 'user'` notifications. Required for a subscription to ever match a user notification. */
  event?: string | string[];
  handler: RealtimeNotificationHandler;
  id: string;
  operation?: string | string[];
  resource?: string | string[];
}

export class EventDispatcher {
  private currentTenantId: string | null = null;
  private nextId = 0;
  private subscriptions = new Map<string, Subscription>();

  setTenantId(tenantId: string): void {
    this.currentTenantId = tenantId;
  }

  subscribe(options: Omit<Subscription, 'id'>): string {
    const id = `sub-${++this.nextId}`;
    this.subscriptions.set(id, { ...options, id });
    return id;
  }

  unsubscribe(id: string): void {
    this.subscriptions.delete(id);
  }

  dispatch(notification: PharosRealtimeNotification): void {
    if (this.currentTenantId && notification.tenantId && this.currentTenantId !== notification.tenantId) {
      return;
    }

    for (const subscription of this.subscriptions.values()) {
      if (!this.matchesFilter(notification, subscription)) {
        continue;
      }

      try {
        const result = subscription.handler(notification);
        const resultAsObj = result as { catch?: unknown };
        if (result != null && typeof resultAsObj.catch === 'function') {
          (result as Promise<unknown>).catch((error: unknown) => {
            console.error('[EventDispatcher] Async handler error:', error);
          });
        }
      } catch (error) {
        console.error('[EventDispatcher] Sync handler error:', error);
      }
    }
  }

  clear(): void {
    this.subscriptions.clear();
  }

  // ─── Private ─────────────────────────────────────────────────────────────

  /**
   * `resource` notifications keep their pre-existing resource/operation matching, but never match
   * a subscription that declared an `event` filter — that subscription is only interested in
   * `user` notifications. `user` notifications only match subscriptions that declared an `event`
   * filter, matched against `notification.event` — an event-less subscription (e.g. a plain
   * `resource` subscription) must never start receiving user events.
   */
  private matchesFilter(notification: PharosRealtimeNotification, subscription: Subscription): boolean {
    if (notification.type === 'resource') {
      if (subscription.event) return false;
      return (
        this.matchesCriteria(notification.resource, subscription.resource) &&
        this.matchesCriteria(notification.operation, subscription.operation)
      );
    }
    if (notification.type === 'user') {
      if (!subscription.event) return false;
      return this.matchesCriteria(notification.event, subscription.event);
    }
    return false;
  }

  private matchesCriteria(value: string, filter: string | string[] | undefined): boolean {
    if (!filter) return true;
    if (Array.isArray(filter)) return filter.includes(value);
    return filter === value;
  }
}
